import { rangeTypes } from '@dataplace.ai/constants'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { MapTile } from '@dataplace.ai/ui-components/atoms'
import { isFeatureStyleGroup, ITrafficTypes, useTrafficTypes } from '@placeme/components/organisms/Tiles/Traffic/TrafficVisualisation/TrafficVisualisationTile'
import { IExtendedFeatureCollection } from '@placeme/components/organisms/Tiles/Visualization/TrafficVisualisationMap/TrafficVisualisationMapTile'
import { BasicLayers, FeatureCollectionLayers, GeojsonLayers, WMSLayers } from '@dataplace.ai/ui-components/atoms/MapTile/@types/LayersTypes'
import { IGeojson } from '@dataplace.ai/ui-components/atoms/MapTile/components/MapOverlays/@types/IGeojson'
import { RootState } from 'apps/placeme/src/redux/store'
import useCheckIfPrinting from 'apps/placeme/src/customHooks/useCheckIfPrinting'
import CatchmentInfoComponent from '../components/CatchmentInfoComponent'
import { StyledWrapper, TitleWrapper } from '../SharedOverview.styles'
import { NoDataGuard } from '../components/NoDataGuard'
import { NO_OVERVIEW_DATA } from '../constants'

interface TrafficMapProps {
  catchment: {
    mode: (typeof rangeTypes)[keyof typeof rangeTypes]
    length: number
    coordinates: IGeojson['data']['coordinates']
  }
  mapData: {
    features: {
      geometry: {
        coordinates: number[],
        type: string,
      },
      properties: {
        style: {
          color: string,
          fillColor: string,
          weight: number,
        }
      },
      type: string
    }[]
    type: string
  } | typeof NO_OVERVIEW_DATA
}

export type TrafficMapData = TrafficMapProps | typeof NO_OVERVIEW_DATA
const TILE_TITLE_KEY = 'placeme.traffic_visualisation_tile.section_sub_title_2_span_1'

function TRAFFIC_MAP({
  catchment,
  mapData,
}: TrafficMapProps): JSX.Element {
  const { t } = useTranslation()
  const { value } = useSelector((state: RootState) => state.location)
  const { isPrinting } = useCheckIfPrinting()
  const {
    setTrafficTypes,
    selectedTrafficType,
    TrafficTypeDropdown,
  } = useTrafficTypes()

  const getLayers = () => {
    if (!mapData || mapData === NO_OVERVIEW_DATA) {
      return undefined
    }
    const features: IExtendedFeatureCollection['data']['features'] = []
    mapData?.features?.forEach((feature, index) => {
      if (isFeatureStyleGroup(feature.properties.style)) {
        if (index === 0) {
          const newTypes: Record<string, string> = {
            total: 'total',
          }
          Object.keys(feature.properties.style).forEach(type => {
            newTypes[type] = type
          })
          setTrafficTypes(newTypes as unknown as ITrafficTypes)
        }
        features.push({
          geometry: feature.geometry,
          properties: {
            ...feature.properties,
            style: feature.properties.style[selectedTrafficType],
          },
          type: 'Feature',
        })
      } else {
        features.push({
          geometry: feature.geometry,
          properties: {
            ...feature.properties,
            style: feature.properties.style,
          },
          type: 'Feature',
        })
      }
    })

    const layers: (BasicLayers | GeojsonLayers | WMSLayers | FeatureCollectionLayers)[] = [
      {
        id: 'traffic_layer_overview_map',
        layer: {
          data: {
            features,
            type: 'FeatureCollection',
          },
          options: {
            type: 'geojson',
            id: 'traffic_layer_map',
          },
        },
      }]

    // range layer
    if (catchment.coordinates) {
      layers.push({
        id: 'traffic_layer_overview_map_catchment',
        layer: {
          data: {
            coordinates: catchment.coordinates,
            type: 'Polygon',
            properties: {},
          },
          options: {
            type: 'geojson',
            id: 'traffic_range',
            style: {
              color: '#0000a2',
              fillColor: '#0000a2',
              weight: 1.5,
              fillOpacity: 0.05,
            },
          },
        },
      })
    }

    return layers
  }

  const mapLayers = useMemo(() => getLayers(), [selectedTrafficType, JSON.stringify(mapData)])

  return (
    <StyledWrapper>
      <TitleWrapper>
        {t(TILE_TITLE_KEY)}
        <CatchmentInfoComponent
          length={catchment.length}
          mode={catchment.mode}
        />
        {TrafficTypeDropdown}
      </TitleWrapper>
      <MapTile
        dragging
        height={isPrinting ? '150px' : '300px'}
        layers={mapLayers}
        location={value}
        mapId='example-map-data-tile'
        pinDisplayed
        popupHeading={`${t('generic.chosen_location')}:`}
        popupParagraph={value?.address}
        showScaleControl
        width='100%'
        zoom={isPrinting ? 14.75 : 15}
        zoomControl
      />
    </StyledWrapper>
  )
}

export function TrafficMap({ data }: {data: TrafficMapData}): JSX.Element {
  return (
    <NoDataGuard
      component={TRAFFIC_MAP}
      data={data}
      title={TILE_TITLE_KEY}
    />
  )
}
